import { useGetContractedCompanies } from "@/entities/get-contracted-companies/fetch";
import { usePostOffersCargos } from "@/entities/post-offers-cargos/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { transformLoadable } from "@/shared/lib/loadable";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { AdminCargoCreatePageProps, DraftOfferCargo } from "../model";

const concatAddress = (draftOfferCargo: DraftOfferCargo) => {
  const destinations = draftOfferCargo.destinations.map((destination) => ({
    ...destination,
    city: destination.city,
    addressLine: destination.addressLine,
    address: destination.addressLine
      ? `${destination.city} ${destination.addressLine}`
      : destination.city,
  }));
  return { ...draftOfferCargo, destinations };
};

export const useAdminCargoCreatePageProps = (): AdminCargoCreatePageProps => {
  // State
  const [previewValues, setPreviewValues] =
    useState<AdminCargoCreatePageProps["previewValues"]>(null);

  // Local Storage
  // 記入途中の情報をローカルストレージに保存する
  const [inProgressValues, setInProgressValues, removeInProgressValues] =
    useInProgressValuesLocalCache<
      AdminCargoCreatePageProps["defaultInputValues"]
    >("AdminCargoCreatePage");

  // Backend API
  const getCompaniesQuery = useGetContractedCompanies(
    {
      pagination: { displayLimit: 10000 },
    },
    { enabled: true },
  );
  const postOffersCargos = usePostOffersCargos();

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Actions
  const navigate = useNavigate();

  const onInputValuesUpdate = useCallback<
    AdminCargoCreatePageProps["onInputValuesUpdate"]
  >(
    (draftOfferCargo) => {
      setInProgressValues(draftOfferCargo);
    },
    [setInProgressValues],
  );

  const onConfirmClick = useCallback<
    AdminCargoCreatePageProps["onConfirmClick"]
  >(async (draftOfferCargo) => {
    setPreviewValues(draftOfferCargo);
  }, []);

  const onBackToInProgressClick = useCallback<
    AdminCargoCreatePageProps["onBackToInProgressClick"]
  >(() => {
    setPreviewValues(null);
  }, []);

  const onSubmitClick = useCallback<AdminCargoCreatePageProps["onSubmitClick"]>(
    async (draftOfferCargo) => {
      const response = await postOffersCargos.mutateAsync({
        data: {
          ...concatAddress(draftOfferCargo),
          vehicleId: null,
        },
      });
      if (response && isSuccessStatusCode(response.status)) {
        setGlobalNotification({
          variant: "success",
          message: "荷物の新規登録が完了しました",
          autoHide: true,
        });
        removeInProgressValues();
        navigate({
          to: "/admin/cargos",
        });
      }
    },
    [
      postOffersCargos.mutateAsync,
      removeInProgressValues,
      setGlobalNotification,
      navigate,
    ],
  );

  const companies = useMemo(() => {
    return transformLoadable(
      loadableFromUseQueryResult(getCompaniesQuery),
      (data) =>
        data.data.companies.filter((c) => c.category?.includes("shipper")),
    );
  }, [getCompaniesQuery]);

  return {
    companies,
    defaultInputValues: inProgressValues,
    previewValues,
    onInputValuesUpdate,
    onBackToInProgressClick,
    onSubmitClick,
    onConfirmClick,
  };
};
